<template>
  <div class="bom__container">

    <div class="bom__info">

      <button 
        class="button is-link is-fullwidth mb-5" 
        :class="{ 'is-loading': saving }"
        :disabled="!valid || saving"
        @click.stop="saveBom()">
        <span class="icon is-medium">
          <i class="fas fa-save"></i>
        </span>
        <span>              
          Save Bom
        </span>
      </button>

      <v-form :valid.sync="valid">
        <div class="field">
          <label class="label">Project*</label>
          <search-autocomplete
            v-model="bom.project_id">
          </search-autocomplete>
        </div>
        
        <div class="field">
          <label class="label">BOM Name*</label>
          <v-text-field 
            required
            placeholder="Enter BOM Name"
            v-model="bom.name">
          </v-text-field>
        </div>
      </v-form>

    </div>

    <div class="bom__details">

      <div class="level">
        <div class="level-left buttons mb-0">
          <a class="button is-primary" @click="addRow()">
            Add Product to BOM
          </a>
          <a 
            class="button is-primary" 
            :disabled="!bom.project_id"
            @click="addingSlabItems = true">
            <span v-if="!bom.project_id">
              Select a project to add slab items
            </span>
            <span v-else>
              Add Existing Slab Items to BOM
            </span>
          </a>
          <a class="button" @click="addingProduct = true">
            Add a New Product
          </a>
        </div>

        <div class="level-right buttons mb-0">
          <a class="button" 
            v-if="selected.length > 0"
            @click="deleteSelected()">
            <span class="icon is-medium">
              <i class="fas fa-trash has-text-danger"></i>
            </span>
            <span>Delete {{ selected.length }} Detail<span v-if="selected.length > 0">s</span></span>
          </a>
        </div>
      </div>

      <div v-if="slabItems.length > 0" class="mb-5">
        <header class="menu-label">
          Slabs
        </header>
        <table 
          class="table is-hoverable is-fullwidth">
          <thead>
            <tr>
              <th 
                v-for="(header, headerIndex) in slabItemHeaders" 
                :key="headerIndex">
                {{ header}}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr 
              class="v-centered"
              v-for="item in slabItems"
              :key="item.id">
              <td>
                {{ item.backing_product_master.name }}
              </td>
              <td>
                {{ item.facing_product_master.name }}
              </td>
              <td>
                {{ item.slab_product_master.name }}
              </td>
              <td>
                {{ lwt(item) }}
              </td>
              <td>
                {{ item.finished_qty }}
              </td>
              <td>
                {{ item.location }}
              </td>
              <td>
                {{ item.description }}
              </td>
              <td>
                <button class="button is-small" @click="deselectSlabItem(item.id)">
                  <span class="icon">
                    <i class="fas fa-trash has-text-grey"></i>
                  </span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <hr v-if="slabItems.length > 0"/>

      <header class="menu-label" v-if="slabItems.length > 0">
        Other Products
      </header>

      <table class="table is-hoverable is-fullwidth">

        <thead>
          <tr>
            <th>
              <v-checkbox
                :value="allSelected"
                @input="toggleAllSelected()">
              </v-checkbox>
            </th>
            <th 
              v-for="(header, headerIndex) in headers" 
              :key="headerIndex">
              {{ header.label }}
            </th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <template v-for="(detail, i) in bomDetails">
            <bom-maker-detail-row 
              :key="i"
              :value="detail"
              @remove="removeRow($event)"
              @preview="preview.src = $event; preview.state = true"/>
          </template>
        </tbody>

      </table>

    </div>

    <add-product-modal v-model="addingProduct"/>

    <v-file-preview 
      v-model="preview.state"
      :src="preview.src"/>

    <add-slabs-to-bom-modal
      ref="add-slabs-to-bom"
      :project-id="bom.project_id"
      v-model="addingSlabItems"
      @select="selectSlabItem($event)"
      @deselect="removeSlabItem($event)"/>

  </div>
</template>

<script>
import VForm from '@/components/base/VForm'
import VTextField from '@/components/base/VTextField'
import VCheckbox from '@/components/base/VCheckbox'
import VFilePreview from '@/components/base/VFilePreview'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'

import BomMakerDetailRow from '@/components/inventory/BomMakerDetailRow'
import AddProductModal from '@/components/inventory/AddProductModal'
import AddSlabsToBomModal from '@/components/inventory/AddSlabsToBomModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VTextField,
    VForm,
    VCheckbox,
    VFilePreview,
    AddProductModal,
    SearchAutocomplete,
    BomMakerDetailRow,
    AddSlabsToBomModal
  },
  data() {
    return {
      defaultRows: 1,
      bom: {
        name: null,
        project_id: null
      },
      bomDetails: [],
      slabItems: [],
      selected: [],
      allSelected: false,
      headers: [
        { label: 'Product Category', field: 'product_category_id', default: null },
        { label: 'Product', field: 'product_master_id', default: null },
        { label: 'Product Spec', field: '', default: null },
        { label: 'Product Sample Image', field: '', default: null },
        { label: 'L"', field: 'length_in_str', default: null },
        { label: 'W"', field: 'width_in_str', default: null },
        { label: 'T"', field: 'thick_in_str', default: null },
        { label: 'Finished Quantity', field: 'finished_qty', default: null },
        { label: 'Product Unit', field: '', default: null }
      ],
      slabItemHeaders: [
        'Backing Product', 'Facing Product', 'Finished Slab', 
        'Size', 'Quantity', 'Location', 'Desc', ''
      ],
      valid: false,
      addingProduct: false,
      addingSlabItems: false,
      preview: {
        state: false,
        src: null
      },
      saving: false
    }
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapState("products", ["products", "categories"]),
    template() {
      let obj = {};
      this.headers.forEach(col => {
        obj[col.field] = col.default;
      });

      return obj
    },
    ids() {
      return this.bomDetails.map(d => d.id);
    }
  },
  methods: {
    ...mapActions("boms", ["addBom", "addDetail", "addSlabItem"]),
    ...mapActions("products", ["getProducts", "getCategories"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.categories.length == 0) {
        this.getCategories();
      }

      for (let i=1; i <= this.defaultRows; i++) {
        this.addRow();
      }
    },
    addRow() {
      let newRow = Object.assign({}, this.template);
      newRow.id = this.ids.length == 0 ? 1 : Math.max(...this.ids) + 1;

      this.bomDetails.push(newRow);
    },
    removeRow(id) {
      this.bomDetails.forEach((detail, i) => {
        if (detail.id == id) {
          this.bomDetails.splice(i, 1);
        }
      })
    },
    deselectSlabItem(id) {
      this.$refs['add-slabs-to-bom'].parentDeselect(id);
    },
    selectSlabItem(item) {
      this.slabItems.push(item);
    },
    removeSlabItem(itemId) {
      this.slabItems.forEach((item, i) => {
        if (item.id == itemId) {
          this.slabItems.splice(i, 1);
        }
      })
    },
    lwt(item) {
      return `L${item.length_in_str}" x W${item.width_in_str}" x T${item.s_thick_in_str}"`
    },
    deleteSelected() {
      this.selected.forEach((id, i) => {
        this.bomDetails.forEach(detail => {
          if (detail.id == id) {
            this.bomDetails.splice(i, 1);
          }
        });
      });
      this.selected.splice(0, this.selected.length);
    },
    toggleSelected(id) {
      const index = this.selected.indexOf(id);
      if (index == -1) {
        this.selected.push(id);
      } else {
        this.selected.splice(index, 1);
        if (this.selected.length == 0) {
          this.allSelected = false;
        }
      }
    },
    toggleAllSelected(v) {
      this.allSelected = v;
      const { selected } = this;

      if (v) {
        this.ids.forEach(id => {
          if (selected.indexOf(id) == -1) {
            selected.push(id);
          }
        })
      } else {
        selected.splice(0, selected.length);
      }
    },
    saveBom() {
      this.saving = true;

      this.addBom({ data: this.bom })
        .then(resp => {

          let completedDetails = this.bomDetails.filter(
            d => d.finished_qty != null && d.product_master_id != null
          );
          
          let promises = [];

          completedDetails.forEach(detail => {
            detail.bom_id = resp.data.id;
            promises.push(this.addDetail(detail));
          });

          this.slabItems.forEach(item => {
            promises.push(this.addSlabItem(
              { bom_id: resp.data.id, production_sheet_item_id: item.id}
            ));
          });

          Promise.all(promises).then(() => {
            this.notifySuccess();
          }).catch(() => this.saving = false);
        }).catch(() => this.saving = false);
    },
    notifySuccess() {
      this.saving = false;
      this.setNotification({
        type: 'success',
        status: true,
        message: 'Bom successfully added'
      });
      this.$router.push({ name: 'Boms' });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.bom {
  &__container {
    display: flex;
    flex-direction: row;
  }
  &__info {
    padding: 1.5em;
    width: 15%;
  }
  &__details {
    padding-right: 1.5em;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
    width: 85%;
  }
}
</style>